import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PencilIcon, CheckIcon, XMarkIcon, PhotoIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { getFirestore, doc, getDoc, updateDoc, getDocs, collection } from 'firebase/firestore';
import { facilitiesService } from '../firebase/services';

const SpaceDetail = () => {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const db = getFirestore();

  // State variables
  const [space, setSpace] = useState(null);
  const [editedSpace, setEditedSpace] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [listingCategories, setListingCategories] = useState([]);
  const [ownerDetails, setOwnerDetails] = useState(null);

  const [showFacilitySelector, setShowFacilitySelector] = useState(false);
  const [isAddingPicture, setIsAddingPicture] = useState(false);
  const [uploadMethod, setUploadMethod] = useState('url');
  const [newPictureUrl, setNewPictureUrl] = useState('');
  const [showVehicleTypeSelector, setShowVehicleTypeSelector] = useState(false);

  // Helper function to format dates
  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      if (date.toDate) {
        return date.toDate().toLocaleDateString();
      }
      return new Date(date).toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };



  // Helper function to get facility by value
  const getFacilityByValue = (value) => {
    return facilities.find(f => f.value === value);
  };

  // Helper function to get vehicle icon based on vehicle type
  const getVehicleIcon = (vehicleType) => {
    const type = vehicleType?.toLowerCase() || '';
    
    if (type.includes('truck')) return '🚛';
    if (type.includes('event')) return '🎪';
    if (type.includes('bike')) return '🏍️';
    if (type.includes('exhibit')) return '🎨';
    if (type.includes('bus')) return '🚌';
    if (type.includes('carvan') || type.includes('caravan')) return '🚐';
    if (type.includes('ghost kitchen') || type.includes('kitchen')) return '🍳';
    if (type.includes('sports ground') || type.includes('sports')) return '⚽';
    if (type.includes('ev') || type.includes('electric')) return '🔋';
    if (type.includes('suv')) return '🚙';
    if (type.includes('jcb')) return '🚜';
    if (type.includes('boat')) return '🚢';
    if (type.includes('hoarding')) return '📢';
    if (type.includes('hawker') || type.includes('hawker space')) return '🛒';
    if (type.includes('housing society') || type.includes('housing')) return '🏘️';
    if (type.includes('autorickshaw') || type.includes('auto')) return '🛺';
    if (type.includes('car')) return '🚗';
    
    // Default icon for unknown vehicle types
    return '🚗';
  };

  // Load facilities from Firebase
  const loadFacilities = useCallback(async () => {
    try {
      const facilitiesData = await facilitiesService.getAllFacilities();
      setFacilities(facilitiesData);
    } catch (err) {
      console.error('Error loading facilities:', err);
      setFacilities([]);
    }
  }, []);

  // Load owner details from Firebase
  const loadOwnerDetails = useCallback(async (ownerId) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', ownerId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setOwnerDetails({
          firstName: userData.firstname || 'N/A',
          lastName: userData.lastname || 'N/A',
          email: userData.email || 'N/A'
        });
      }
    } catch (err) {
      console.error('Error loading owner details:', err);
      setOwnerDetails(null);
    }
  }, [db]);

  // Load space data from Firebase
  const loadSpaceData = useCallback(async () => {
    try {
      setLoading(true);
      const spaceDoc = await getDoc(doc(db, 'spaces', spaceId));
      
      if (!spaceDoc.exists()) {
        setError('Space not found');
        return;
      }

      const spaceData = {
        id: spaceDoc.id,
        ...spaceDoc.data()
      };
      
      setSpace(spaceData);
      setEditedSpace(spaceData);
      
      // Load owner details if owner ID exists
      if (spaceData.owner) {
        loadOwnerDetails(spaceData.owner);
      }
    } catch (err) {
      setError('Failed to load space details');
      console.error('Error loading space data:', err);
    } finally {
      setLoading(false);
    }
  }, [spaceId, db, loadOwnerDetails]);

  // Load listing categories from Firebase
  const loadListingCategories = useCallback(async () => {
    try {
      const categoriesSnapshot = await getDocs(collection(db, 'listingcategories'));
      const categoriesData = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setListingCategories(categoriesData);
    } catch (err) {
      console.log('Error loading listing categories:', err);
      setListingCategories([]);
    }
  }, [db]);

  useEffect(() => {
    if (spaceId) {
      loadSpaceData();
      loadListingCategories();
      loadFacilities();
    }
  }, [spaceId, loadSpaceData, loadListingCategories, loadFacilities]);

  // Event handlers
  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedSpace(space);
  };

  const handleSave = async () => {
    try {
      await updateDoc(doc(db, 'spaces', spaceId), editedSpace);
      setSpace(editedSpace);
      setIsEditing(false);
    } catch (err) {
      setError('Failed to update space');
      console.error('Error updating space:', err);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedSpace(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleVehicleFeatureChange = (vehicleType, field, value) => {
    setEditedSpace(prev => {
      const currentFeatures = prev.vehicleFeature?.[vehicleType] || {};
      let newValue = value;
      
      if (field === 'available') {
        const availableCount = parseInt(value) || 0;
        newValue = availableCount;
      }
      
      return {
        ...prev,
        vehicleFeature: {
          ...prev.vehicleFeature,
          [vehicleType]: {
            ...currentFeatures,
            [field]: newValue
          }
        }
      };
    });
  };

  const handleAddVehicleType = () => {
    setShowVehicleTypeSelector(true);
  };

  const handleAddVehicleTypeFromCategory = (categoryId) => {
    const category = listingCategories.find(cat => cat.id === categoryId);
    if (category) {
      setEditedSpace(prev => ({
        ...prev,
        vehicleFeature: {
          ...prev.vehicleFeature,
          [category.value]: {
            available: 0,
            hourlyRate: 0,
            dailyRate: 0,
            weeklyRate: 0,
            monthlyRate: 0
          }
        }
      }));
      setShowVehicleTypeSelector(false);
    }
  };

  const handleRemoveVehicleType = (vehicleType) => {
    setEditedSpace(prev => {
      const newVehicleFeature = { ...prev.vehicleFeature };
      delete newVehicleFeature[vehicleType];
      return {
        ...prev,
        vehicleFeature: newVehicleFeature
      };
    });
  };

  const handleAddFacility = (facilityId) => {
    const facility = facilities.find(f => f.id === facilityId);
    if (facility) {
      setEditedSpace(prev => ({
        ...prev,
        parkingFeature: {
          ...prev.parkingFeature,
          [facility.value]: true
        }
      }));
    }
  };

  const handleRemoveFacility = (facilityId) => {
    const facility = facilities.find(f => f.id === facilityId);
    if (facility) {
      setEditedSpace(prev => {
        const newParkingFeature = { ...prev.parkingFeature };
        delete newParkingFeature[facility.value];
        return {
          ...prev,
          parkingFeature: newParkingFeature
        };
      });
    }
  };

  const handleFacilityValueChange = (facilityId, value) => {
    const facility = facilities.find(f => f.id === facilityId);
    if (facility) {
      setEditedSpace(prev => ({
        ...prev,
        parkingFeature: {
          ...prev.parkingFeature,
          [facility.value]: value
        }
      }));
    }
  };



  const handleRemoveParkingFeature = (featureKey) => {
    setEditedSpace(prev => {
      const newParkingFeature = { ...prev.parkingFeature };
      delete newParkingFeature[featureKey];
      return {
        ...prev,
        parkingFeature: newParkingFeature
      };
    });
  };

  const handleAddPicture = () => {
    if (newPictureUrl.trim()) {
      setEditedSpace(prev => ({
        ...prev,
        photos: [...(prev.photos || []), newPictureUrl.trim()]
      }));
      setNewPictureUrl('');
      setIsAddingPicture(false);
    }
  };

  const handleRemovePicture = (index) => {
    setEditedSpace(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Handle file upload logic here
      console.log('File selected:', file);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading space details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️</div>
          <p className="text-gray-800 text-lg mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!space) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600">Space not found</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md mt-4"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <h1 className="ml-3 text-xl font-semibold text-gray-900">
                Space Details
              </h1>
            </div>
            
            <div className="flex items-center space-x-3">
              {!isEditing ? (
                <button
                  onClick={handleEdit}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Edit
                </button>
              ) : (
                <div className="flex space-x-3">
                  <button
                    onClick={handleCancel}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <XMarkIcon className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <CheckIcon className="h-4 w-4 mr-2" />
                    Save
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Basic Information Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">Space Name</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedSpace.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="mt-1 text-sm text-gray-900">{space.name || 'N/A'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Space Code</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedSpace.SpaceCodeNo || ''}
                  onChange={(e) => handleInputChange('SpaceCodeNo', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="mt-1 text-sm text-gray-900">{space.SpaceCodeNo || 'N/A'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Category</label>
              {isEditing ? (
                <select
                  value={editedSpace.Type || ''}
                  onChange={(e) => handleInputChange('Type', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Category</option>
                  {listingCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="mt-1 text-sm text-gray-900">
                  {listingCategories.find(cat => cat.id === space.Type)?.name || space.Type || 'N/A'}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Free</label>
              {isEditing ? (
                <select
                  value={editedSpace.Free ? 'true' : 'false'}
                  onChange={(e) => handleInputChange('Free', e.target.value === 'true')}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              ) : (
                <p className="mt-1 text-sm text-gray-900">{space.Free ? 'Yes' : 'No'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Allowed</label>
              {isEditing ? (
                <select
                  value={editedSpace.allowed ? 'true' : 'false'}
                  onChange={(e) => handleInputChange('allowed', e.target.value === 'true')}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </select>
              ) : (
                <p className="mt-1 text-sm text-gray-900">{space.allowed ? 'Yes' : 'No'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Available</label>
              <p className="mt-1 text-sm text-gray-900">{space.available || 0}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Opt Number</label>
              <p className="mt-1 text-sm text-gray-900">{space.optnum || 'N/A'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Space Owner Code</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedSpace.spaceownercode || ''}
                  onChange={(e) => handleInputChange('spaceownercode', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="mt-1 text-sm text-gray-900">{space.spaceownercode || 'N/A'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Owner</label>
              <p className="mt-1 text-sm text-gray-900">
                {ownerDetails ? `${ownerDetails.firstName} ${ownerDetails.lastName}` : 'N/A'}
              </p>
              {ownerDetails && (
                <p className="mt-1 text-xs text-gray-500">{ownerDetails.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Approved</label>
              {isEditing ? (
                <select
                  value={editedSpace.approved ? 'true' : 'false'}
                  onChange={(e) => handleInputChange('approved', e.target.value === 'true')}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="false">Pending Approval</option>
                  <option value="true">Approved</option>
                </select>
              ) : (
                <div className="mt-1 flex items-center">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    space.approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {space.approved ? 'Approved' : 'Pending Approval'}
                  </span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Owner Phone</label>
              <p className="mt-1 text-sm text-gray-900">{space.phone || 'N/A'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Created Date</label>
              <p className="mt-1 text-sm text-gray-900">{formatDate(space.created)}</p>
            </div>
          </div>
        </div>

        {/* Description Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Description</h2>
          
          {isEditing ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={editedSpace.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                placeholder="Enter detailed description of the parking space..."
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-900 whitespace-pre-wrap">
                {space.description || 'No description available'}
              </p>
            </div>
          )}
        </div>

        {/* Search Keywords Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Search Keywords</h2>
          
          <div>
            {space.searchKeywords && space.searchKeywords.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {space.searchKeywords.map((keyword, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No search keywords configured</p>
            )}
          </div>
        </div>

        {/* Address Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Address Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500">Address 1</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedSpace.address1 || ''}
                  onChange={(e) => handleInputChange('address1', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="mt-1 text-sm text-gray-900">{space.address1 || 'N/A'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Address 2</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedSpace.address2 || ''}
                  onChange={(e) => handleInputChange('address2', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="mt-1 text-sm text-gray-900">{space.address2 || 'N/A'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">City</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedSpace.city || ''}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="mt-1 text-sm text-gray-900">{space.city || 'N/A'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">State</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedSpace.state || ''}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="mt-1 text-sm text-gray-900">{space.state || 'N/A'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">ZIP Code</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedSpace.zipcode || ''}
                  onChange={(e) => handleInputChange('zipcode', e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="mt-1 text-sm text-gray-900">{space.zipcode || 'N/A'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Location (Coordinates)</label>
              <p className="mt-1 text-sm text-gray-900">
                {space.location ? `${space.location.latitude}, ${space.location.longitude}` : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Photos Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Photos</h2>
          
          {isEditing && (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-4 mb-4">
                <button
                  onClick={() => setIsAddingPicture(true)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md font-medium flex items-center"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Picture
                </button>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="url"
                    name="uploadMethod"
                    value="url"
                    checked={uploadMethod === 'url'}
                    onChange={(e) => setUploadMethod(e.target.value)}
                  />
                  <label htmlFor="url" className="text-sm">URL</label>
                  <input
                    type="radio"
                    id="file"
                    name="uploadMethod"
                    value="file"
                    checked={uploadMethod === 'file'}
                    onChange={(e) => setUploadMethod(e.target.value)}
                  />
                  <label htmlFor="file" className="text-sm">File Upload</label>
                </div>
              </div>
              
              {isAddingPicture && (
                <div className="space-y-3">
                  {uploadMethod === 'url' ? (
                    <div className="flex space-x-2">
                      <input
                        type="url"
                        placeholder="Enter image URL"
                        value={newPictureUrl}
                        onChange={(e) => setNewPictureUrl(e.target.value)}
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={handleAddPicture}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
                      >
                        Add
                      </button>
                      <button
                        onClick={() => {
                          setIsAddingPicture(false);
                          setNewPictureUrl('');
                        }}
                        className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex space-x-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileUpload}
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={() => setIsAddingPicture(false)}
                        className="btn-secondary px-4 py-2"
                      >
                        Done
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(editedSpace?.photos || space?.photos || []).map((photo, index) => (
              <div key={index} className="relative group">
                <img
                  src={photo}
                  alt={`Space photo ${index + 1}`}
                  className="w-full h-48 object-cover rounded-lg border border-gray-200"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Found';
                  }}
                />
                {isEditing && (
                  <button
                    onClick={() => handleRemovePicture(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {(!editedSpace?.photos || editedSpace.photos.length === 0) && 
             (!space?.photos || space.photos.length === 0) && (
              <div className="col-span-full text-center py-8 text-gray-500">
                <PhotoIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No photos available</p>
              </div>
            )}
          </div>
        </div>

        {/* Vehicle Features & Pricing Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Vehicle Features & Pricing</h2>
          
          <div className="space-y-4">
            {isEditing && (
              <div className="mb-4">
                <button
                  onClick={handleAddVehicleType}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium flex items-center"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Vehicle Type from Categories
                </button>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vehicle Type
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Count
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Available Lots
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hourly Rate (₹)
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Daily Rate (₹)
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Weekly Rate (₹)
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Monthly Rate (₹)
                    </th>
                    {isEditing && (
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Object.entries(editedSpace?.vehicleFeature || space?.vehicleFeature || {}).map(([vehicleType, features]) => (
                    <tr key={vehicleType}>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-lg mr-2">{getVehicleIcon(vehicleType)}</span>
                          <span className="text-sm font-medium text-gray-900">{vehicleType}</span>
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{features.available || 0}</span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        {isEditing ? (
                          <input
                            type="number"
                            value={features.available || 0}
                            onChange={(e) => handleVehicleFeatureChange(vehicleType, 'available', parseInt(e.target.value) || 0)}
                            className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <span className="text-sm text-gray-900">{features.available || 0}</span>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        {isEditing ? (
                          <input
                            type="number"
                            value={features.hourlyRate || 0}
                            onChange={(e) => handleVehicleFeatureChange(vehicleType, 'hourlyRate', parseFloat(e.target.value) || 0)}
                            className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <span className="text-sm text-gray-900">₹{features.hourlyRate || 0}</span>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        {isEditing ? (
                          <input
                            type="number"
                            value={features.dailyRate || 0}
                            onChange={(e) => handleVehicleFeatureChange(vehicleType, 'dailyRate', parseFloat(e.target.value) || 0)}
                            className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <span className="text-sm text-gray-900">₹{features.dailyRate || 0}</span>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        {isEditing ? (
                          <input
                            type="number"
                            value={features.weeklyRate || 0}
                            onChange={(e) => handleVehicleFeatureChange(vehicleType, 'weeklyRate', parseFloat(e.target.value) || 0)}
                            className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <span className="text-sm text-gray-900">₹{features.weeklyRate || 0}</span>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        {isEditing ? (
                          <input
                            type="number"
                            value={features.monthlyRate || 0}
                            onChange={(e) => handleVehicleFeatureChange(vehicleType, 'monthlyRate', parseFloat(e.target.value) || 0)}
                            className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        ) : (
                          <span className="text-sm text-gray-900">₹{features.monthlyRate || 0}</span>
                        )}
                      </td>

                      {isEditing && (
                        <td className="px-3 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleRemoveVehicleType(vehicleType)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Remove vehicle type"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Parking Features Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Parking Features</h2>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Facilities from the collection are mapped to display names (e.g., "on_duty_guard" shows as "Security Guard").
              </p>
              {isEditing && (
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowFacilitySelector(true)}
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md text-sm font-medium flex items-center"
                  >
                    <PlusIcon className="w-4 h-4 mr-1" />
                    Add from Collection
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(editedSpace?.parkingFeature || space?.parkingFeature || {}).map(([featureValue, featureEnabled]) => {
                const facility = getFacilityByValue(featureValue);
                const displayName = facility ? facility.name : featureValue;
                
                return (
                  <div key={featureValue} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <input
                      type="checkbox"
                      id={featureValue}
                      checked={featureEnabled}
                      onChange={(e) => {
                        if (isEditing) {
                          if (facility) {
                            handleFacilityValueChange(facility.id, e.target.checked);
                          } else {
                            handleInputChange('parkingFeature', {
                              ...editedSpace.parkingFeature,
                              [featureValue]: e.target.checked
                            });
                          }
                        }
                      }}
                      disabled={!isEditing}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor={featureValue} className="flex-1 text-sm font-medium text-gray-900">
                      {displayName}
                    </label>
                    {isEditing && (
                      <button
                        onClick={() => {
                          if (facility) {
                            handleRemoveFacility(facility.id);
                          } else {
                            handleRemoveParkingFeature(featureValue);
                          }
                        }}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Remove feature"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {Object.keys(editedSpace?.parkingFeature || space?.parkingFeature || {}).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p>No parking features configured</p>
              </div>
            )}
          </div>
        </div>


      </div>



      {/* Facility Selector Modal */}
      {showFacilitySelector && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white max-h-96 overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Facilities to Add</h3>
              <div className="space-y-2">
                {facilities
                  .filter(facility => !(editedSpace?.parkingFeature || space?.parkingFeature || {})[facility.value])
                  .map(facility => (
                    <div key={facility.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                      <span className="text-sm text-gray-900">{facility.name}</span>
                      <button
                        onClick={() => {
                          handleAddFacility(facility.id);
                          setShowFacilitySelector(false);
                        }}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Add facility"
                      >
                        <PlusIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                {facilities.filter(facility => !(editedSpace?.parkingFeature || space?.parkingFeature || {})[facility.value]).length === 0 && (
                  <div className="text-center py-4 text-gray-500">
                    <p>All available facilities are already added</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowFacilitySelector(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Type Selector Modal */}
      {showVehicleTypeSelector && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white max-h-96 overflow-y-auto">
            <div className="mt-3">
              {/* Top Close Button */}
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Select Vehicle Type from Categories</h3>
                <button
                  onClick={() => setShowVehicleTypeSelector(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
                  title="Close modal"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-2">
                {listingCategories
                  .filter(category => !(editedSpace?.vehicleFeature || space?.vehicleFeature || {})[category.value])
                  .map(category => (
                    <div key={category.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded border border-gray-200">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{category.ContextValue || 'No Context Value'}</div>
                        {category.value && category.value !== category.ContextValue && (
                          <div className="text-sm text-gray-500">Value: {category.value}</div>
                        )}
                        <div className="text-xs text-gray-400">ID: {category.id}</div>
                      </div>
                      <button
                        onClick={() => handleAddVehicleTypeFromCategory(category.id)}
                        className="ml-3 bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full transition-colors"
                        title="Add vehicle type"
                      >
                        <PlusIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                {listingCategories.filter(category => !(editedSpace?.vehicleFeature || space?.vehicleFeature || {})[category.value]).length === 0 && (
                  <div className="text-center py-4 text-gray-500">
                    <p>All available vehicle types are already added</p>
                  </div>
                )}
              </div>
              
              {/* Bottom Close Button */}
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowVehicleTypeSelector(false)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-md font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpaceDetail;